import {
    type ViewStyle,
    type Animated,
    StyleSheet
} from "react-native";
import {
    type AnimatedIconAnimationConstantType,
    type AnimatedIconDurationConstantType,
    type AnimatedIconTriggerConstantType,
    type AnimatedIconPlayableAnimation,
    type AnimatedIconStyleConstantType,
    type AnimatedIconDynamicStyleType,
    type AnimatedIconAnimation,
    type AnimatedIconProperty,
    type AnimatedIconTrigger
} from "./type";
import {
    ANIMATED_ICON_SPECS
} from "./constant";
import {
    getAnimatedIconRange
} from "./util";
import type {
    Mutable
} from "../../types";

export const ANIMATED_ICON_FALLBACKS: Record<AnimatedIconAnimation, AnimatedIconPlayableAnimation> = {
    signature: "appear",
    appear: "appear",
    wiggle: "wiggle",
    trace: "appear",
    draw: "appear",
    pulse: "pulse",
    none: "none",
    spin: "spin",
    pop: "pop"
};

export const ANIMATED_ICON_TRIGGERS: Record<AnimatedIconAnimation, AnimatedIconTrigger> = {
    signature: "mount",
    appear: "mount",
    wiggle: "mount",
    draw: "mount",
    none: "mount",
    pulse: "loop",
    trace: "loop",
    spin: "loop",
    pop: "mount"
};

export const getAnimatedIconAnimation = ({
    animation,
    isParts
}: AnimatedIconAnimationConstantType): AnimatedIconPlayableAnimation => {
    if(isParts) {
        return "none";
    }

    return ANIMATED_ICON_FALLBACKS[animation] ?? "none";
};

export const getAnimatedIconTrigger = ({
    animationTrigger,
    animation
}: AnimatedIconTriggerConstantType): AnimatedIconTrigger => {
    if(animation === "signature" && animationTrigger === "loop") {
        return ANIMATED_ICON_TRIGGERS.signature;
    }

    return animationTrigger ?? ANIMATED_ICON_TRIGGERS[animation];
};

export const getAnimatedIconDuration = ({
    animation,
    durations,
    plan
}: AnimatedIconDurationConstantType): number => {
    if(plan) {
        return plan.duration;
    }

    if(animation === "none") {
        return 0;
    }

    const spec = ANIMATED_ICON_SPECS[animation];

    return durations[spec.duration] * spec.durationScale;
};

export const getAnimatedIconContinuous = ({
    animation,
    plan
}: Pick<AnimatedIconDurationConstantType, "animation" | "plan">): boolean => {
    if(plan) {
        return plan.isContinuous;
    }

    return animation !== "none" && ANIMATED_ICON_SPECS[animation].isContinuous;
};

export const getAnimatedIconStyle = ({
    animation,
    progress
}: AnimatedIconStyleConstantType): Animated.WithAnimatedObject<ViewStyle> => {
    if(animation === "none") {
        return {};
    }

    const spec = ANIMATED_ICON_SPECS[animation];

    const getRange = (property: AnimatedIconProperty) => getAnimatedIconRange({
        keyframes: spec.keyframes,
        easing: spec.easing,
        property
    });

    const style: Animated.WithAnimatedObject<ViewStyle> = {};

    const transform: Array<{ rotate: Animated.AnimatedInterpolation<string> } | { scale: Animated.AnimatedInterpolation<number> }> = [];

    const opacityRange = getRange("opacity");

    const rotateRange = getRange("rotate");

    const scaleRange = getRange("scale");

    if(opacityRange.inputRange.length) {
        style.opacity = progress.interpolate(opacityRange);
    }

    if(rotateRange.inputRange.length) {
        transform.push({
            rotate: progress.interpolate({
                outputRange: rotateRange.outputRange.map((value) => `${value}deg`),
                inputRange: rotateRange.inputRange
            })
        });
    }

    if(scaleRange.inputRange.length) {
        transform.push({
            scale: progress.interpolate(scaleRange)
        });
    }

    if(transform.length) {
        style.transform = transform;
    }

    return style;
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center"
    }
});

export const useStyles = ({
    size
}: AnimatedIconDynamicStyleType) => {
    const styles = {
        container: {
            height: size,
            width: size
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
